// pms-adapters / supabase / cardTaps — the Sextant taps of a date range, each with
// its state (PMS-LEDGER-TAP-VIEW-BRIEF, T1: migrations 20261010200100 / 20261010300100).
//
// Two RPCs: `rec_card_taps` (the taps) and `vega_working_day_latest` (the open-day
// reading and its read time). Like the findings adapter, an answer this build cannot
// read is REFUSED, never shown as a shorter list:
//   - an unknown state, or a row missing its fields → `unknown`, reload the page;
//   - no working-day reading yet → `unknown`, the screen says so;
//   - a missing `fin.findings.read` → `forbidden`.

import {
  err,
  ok,
  type CardTap,
  type CardTapState,
  type CardTapsRange,
  type CardTaps,
  type IsoDate,
  type LedgerCardLine,
  type PropertyCode,
  type Result,
} from '@lemontaigu/pms-core';
import { toPmsError, type SupabaseClient } from './support.js';

/** One row of `rec_card_taps`, verbatim. */
export interface CardTapRow {
  tap_uuid: string;
  tapped_at: string;
  tap_day: string;
  amount_cents: number;
  contract: string | null;
  card_masked: string | null;
  authorisation_ref: string | null;
  state: string;
  payment_id: number | null;
  reconciliation_number: string | null;
  lettering_state: string | null;
  coverage_day: string;
  detail: string;
}

/** One row of `vega_working_day_latest`. */
export interface WorkingDayRow {
  working_day: string;
  read_at: string;
}

const STATES: readonly CardTapState[] = ['rattache', 'hors_vega', 'en_attente_import', 'non_rattache'];
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Returns the taps, plus how many rows could not be read. The caller refuses on any
 *  unreadable row: a shorter list that looks complete is the « all clear » failure. */
export function toCardTaps(rows: CardTapRow[]): { taps: CardTap[]; unreadable: number } {
  const taps: CardTap[] = [];
  let unreadable = 0;
  for (const r of rows) {
    const state = STATES.find((s) => s === r.state);
    if (!state || !DAY.test(r.tap_day) || typeof r.amount_cents !== 'number' || !r.tapped_at) {
      unreadable += 1;
      continue;
    }
    taps.push({
      tapUuid: r.tap_uuid,
      tappedAt: r.tapped_at,
      tapDay: r.tap_day as IsoDate,
      amountCents: r.amount_cents,
      contract: r.contract,
      cardMasked: r.card_masked,
      authorisationRef: r.authorisation_ref,
      state,
      paymentId: r.payment_id,
      reconciliationNumber: r.reconciliation_number,
      detail: r.detail,
    });
  }
  return { taps, unreadable };
}

export class SupabaseCardTaps implements CardTaps {
  constructor(private readonly db: SupabaseClient) {}

  async unmatchedCardLines(propertyCode: PropertyCode, from: IsoDate, to: IsoDate): Promise<Result<LedgerCardLine[]>> {
    const args = { p_property: propertyCode, p_from: from, p_to: to };
    const [tickets, sales] = await Promise.all([
      this.db.rpc('rec_unmatched_card_lines', args),
      this.db.rpc('rec_unmatched_sales', args),
    ]);
    for (const r of [tickets, sales]) {
      if (r.error) {
        if (r.error.code === '42501') {
          return err('forbidden', 'Accès refusé — recherche de rapprochement', { cause: r.error });
        }
        return { ok: false, error: toPmsError(r.error) };
      }
    }
    const lines: LedgerCardLine[] = [];
    for (const [data, origin] of [
      [tickets.data, 'lightspeed'],
      [sales.data, 'vega'],
    ] as const) {
      if (!Array.isArray(data)) {
        return err('unknown', 'Lignes de caisse : réponse illisible', { retryable: false });
      }
      const rows = data as { source_id: string; entry_date: string; amount_cents: number }[];
      if (rows.some((x) => typeof x.source_id !== 'string' || !DAY.test(x.entry_date) || typeof x.amount_cents !== 'number')) {
        return err('unknown', 'Lignes de caisse : une ligne illisible — recharger la page', { retryable: false });
      }
      for (const x of rows) {
        lines.push({ sourceId: x.source_id, day: x.entry_date as IsoDate, amountCents: x.amount_cents, origin });
      }
    }
    return ok(lines);
  }

  async declareMatch(
    propertyCode: PropertyCode,
    tapUuid: string,
    sourceIds: string[],
    reason: string,
  ): Promise<Result<{ diffCents: number }>> {
    const r = await this.db.rpc('fin_declare_tap_match', {
      p_property: propertyCode,
      p_tap: tapUuid,
      p_source_ids: sourceIds,
      p_reason: reason,
    });
    if (r.error) {
      if (r.error.code === '42501') {
        return err('forbidden', 'Accès refusé — rapprochement (rec.taps.audit)', { cause: r.error });
      }
      return err('unknown', String(r.error.message ?? 'Rapprochement refusé'), { retryable: false, cause: r.error });
    }
    const diff = (r.data as { diff_cents?: number } | null)?.diff_cents;
    if (typeof diff !== 'number') {
      return err('unknown', 'Rapprochement : réponse illisible', { retryable: false });
    }
    return ok({ diffCents: diff });
  }

  async declarePassage(
    propertyCode: PropertyCode,
    tapUuid: string,
    barCents: number,
    restoCents: number,
    reason: string,
  ): Promise<Result<{ caseId: string }>> {
    const r = await this.db.rpc('fin_declare_passage', {
      p_property: propertyCode,
      p_tap: tapUuid,
      p_bar_cents: barCents,
      p_resto_cents: restoCents,
      p_reason: reason,
    });
    if (r.error) {
      if (r.error.code === '42501') {
        return err('forbidden', 'Accès refusé — déclaration de vente (rec.taps.audit)', { cause: r.error });
      }
      // The database's own refusal (not a card tap, already declared, amounts not equal to the tap)
      // is shown as it is: it says what to correct.
      return err('unknown', String(r.error.message ?? 'Déclaration refusée'), { retryable: false, cause: r.error });
    }
    const caseId = (r.data as { case_id?: string } | null)?.case_id;
    if (!caseId) {
      return err('unknown', 'Déclaration : réponse illisible', { retryable: false });
    }
    return ok({ caseId });
  }

  async range(
    propertyCode: PropertyCode,
    from: IsoDate,
    to: IsoDate,
    includeMatched: boolean,
  ): Promise<Result<CardTapsRange>> {
    const [taps, day] = await Promise.all([
      this.db.rpc('rec_card_taps', {
        p_property: propertyCode,
        p_from: from,
        p_to: to,
        p_include_matched: includeMatched,
      }),
      this.db.rpc('vega_working_day_latest', { p_property: propertyCode }),
    ]);

    if (taps.error) {
      if (taps.error.code === '42501') {
        return err('forbidden', 'Accès refusé — taps carte', { cause: taps.error });
      }
      if (String(taps.error.message ?? '').includes('jour de travail Vega inconnu')) {
        return err('unknown', 'Jour de travail Vega inconnu : aucune lecture de HPARGEN.DAT', { retryable: false });
      }
      return { ok: false, error: toPmsError(taps.error) };
    }
    if (day.error) {
      return { ok: false, error: toPmsError(day.error) };
    }
    if (!Array.isArray(taps.data) || !Array.isArray(day.data)) {
      return err('unknown', 'Taps carte : réponse illisible', { retryable: false });
    }

    const latest = (day.data as WorkingDayRow[])[0];
    if (!latest || !DAY.test(latest.working_day)) {
      return err('unknown', 'Jour de travail Vega inconnu', { retryable: false });
    }

    const { taps: list, unreadable } = toCardTaps(taps.data as CardTapRow[]);
    if (unreadable > 0) {
      return err('unknown', 'Taps carte : une ligne illisible — recharger la page', { retryable: false });
    }

    const workingDay = latest.working_day as IsoDate;
    const closeDay = (taps.data as CardTapRow[])[0]?.coverage_day ?? shiftBack(workingDay);
    return ok({
      workingDay,
      workingDayReadAt: latest.read_at,
      closeDay: closeDay as IsoDate,
      taps: list,
    });
  }
}

/** The day before an ISO date, for an empty range (no rows carry the close day). */
export function shiftBack(day: string): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}
